import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { testimonialsByDate as testimonials } from '@/data/testimonials'
import { GOOGLE_REVIEWS_URL } from '@/config/site'
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  Heart,
  ThumbsUp
} from 'lucide-react'

const TestimonialsSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [currentTestimonial, setCurrentTestimonial] = useState(0)

  // Gerçek yorum girilmediği sürece bölüm gösterilmez.
  if (testimonials.length === 0) return null


  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }


  return (
    <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6"
          >
            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">Müşteri Yorumları</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            Pet Sahiplerinin
            <span className="text-gradient block">Deneyimleri</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Kliniğimize güvenen pet sahiplerinin deneyimlerini okuyun.
            Can dostlarınızın sağlığı için güvenilir adres olduğumuzu görün.
          </p>

          {/* Rating Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex items-center justify-center space-x-4 mb-8"
          >
            {/* Puan ortalaması burada gösterilmez: aşağıdaki yorumlar Google'daki
                tüm yorumların bir seçkisidir, seçkiden ortalama üretmek yanıltıcı olur.
                Güncel puan ve yorumların tamamı Google profilinde görülebilir. */}
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-primary-300 transition-all"
            >
              <Star className="w-5 h-5 text-yellow-400 fill-current" />
              <span className="font-semibold text-gray-900">Google yorumlarımız</span>
              <span className="text-gray-500 text-sm">— tamamını görün</span>
            </a>
          </motion.div>
        </motion.div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={prevTestimonial}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 group"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600 group-hover:text-primary-600 transition-colors duration-200" />
          </button>
          
          <button
            onClick={nextTestimonial}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 group"
          >
            <ChevronRight className="w-6 h-6 text-gray-600 group-hover:text-primary-600 transition-colors duration-200" />
          </button>

          {/* Testimonials */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mx-8 border border-gray-100"
            >
              <div className="text-center">
                {/* Quote Icon */}
                <div className="inline-flex p-4 bg-primary-100 rounded-full mb-6">
                  <Quote className="w-8 h-8 text-primary-600" />
                </div>

                {/* Rating */}
                <div className="flex items-center justify-center space-x-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < testimonials[currentTestimonial].rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>

                {/* Comment */}
                <blockquote className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8 italic">
                  "{testimonials[currentTestimonial].comment}"
                </blockquote>

                {/* Yorum sahibi — Google profilindeki adıyla */}
                <div className="flex items-center justify-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-primary-700">
                      {testimonials[currentTestimonial].name
                        .split(' ')
                        .map(w => w[0])
                        .slice(0, 2)
                        .join('')}
                    </span>
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-gray-900 text-lg">
                      {testimonials[currentTestimonial].name}
                    </h4>
                    <p className="text-gray-500 text-sm">
                      Google yorumu · {testimonials[currentTestimonial].relativeDate}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dots Indicator */}
          <div className="flex items-center justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentTestimonial
                    ? 'bg-primary-500 w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
        >
          {[
            {
              icon: ThumbsUp,
              title: 'Şeffaf Bilgilendirme',
              description: 'Tedavi planı ve tahmini maliyet önceden paylaşılır'
            },
            {
              icon: Heart,
              title: '7/24 Acil Destek',
              description: 'Mesai dışında da ulaşabilirsiniz'
            },
            {
              icon: Star,
              title: 'Google Yorumları',
              description: 'Puanımızı ve tüm yorumları Google profilimizde görebilirsiniz'
            }
          ].map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              className="text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100"
            >
              <div className="inline-flex p-3 bg-primary-100 rounded-xl mb-4">
                <item.icon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default TestimonialsSection
